import React from 'react';
import { useField } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { Advice } from 'types/Checklist';
import AdviceStatus from 'types/AdviceStatus';

import Popup from 'components/ui/Popup';
import Button, { ButtonSize, ButtonType } from 'components/ui/Button';

type AdviceCardActionsProps = {
  name: string;
  advices: Array<Advice>;
};

const AdviceCardActions: React.FC<AdviceCardActionsProps> = ({ name, advices }) => {
  const [isPopupOpen, setIsPopupOpen] = React.useState(false);

  const { value: adviceId } = useField<string>(`${name}.adviceId`, { subscription: { value: true } }).input;
  const {
    value: status,
    onChange: handleChangeStatus,
  } = useField<AdviceStatus>(`${name}.status`, { subscription: { value: true } }).input;

  const isAlreadyAnswered = React.useMemo(
    () => advices.some((advice) => advice.adviceId === adviceId && advice.status !== AdviceStatus.PENDING),
    [advices, adviceId],
  );

  const handleOnChange = React.useCallback((newStatus: AdviceStatus) => () => {
    handleChangeStatus(newStatus);
  }, [handleChangeStatus]);

  const handleDisabledClick = React.useCallback(() => {
    if (isAlreadyAnswered) {
      setIsPopupOpen(true);
    }
  }, [isAlreadyAnswered]);

  const handleClosePopup = React.useCallback(() => {
    setIsPopupOpen(false);
  }, []);

  return (
    <>
      <div className="relative flex justify-between">
        {isAlreadyAnswered && (
          <div
            className="absolute top-0 left-0 size-full z-10 cursor-not-allowed"
            onClick={handleDisabledClick}
            data-testid="disabledAdviceOverlay"
          />
        )}
        <Button
          testId="refuse"
          size={ButtonSize.SMALL}
          disabled={isAlreadyAnswered}
          className="mr-1.5 min-w-refused max-w-48 px-xs py-0! h-btn"
          onClick={handleOnChange(AdviceStatus.REFUSED)}
          type={status === AdviceStatus.REFUSED ? ButtonType.TERTIARY : ButtonType.PRIMARY}
        >
          <FormattedMessage id="confirm.no" defaultMessage="No" />
        </Button>
        <Button
          testId="accept"
          size={ButtonSize.SMALL}
          className="ml-1.5 w-full min-w-40 py-0! h-btn"
          disabled={isAlreadyAnswered}
          onClick={handleOnChange(AdviceStatus.ACCEPTED)}
          type={status === AdviceStatus.ACCEPTED ? ButtonType.TERTIARY : ButtonType.PRIMARY}
        >
          {status !== AdviceStatus.ACCEPTED && (<FormattedMessage id="basket.add" defaultMessage="Add to basket" />)}
          {status === AdviceStatus.ACCEPTED && (<FormattedMessage id="basket.added" defaultMessage="Added" />)}
        </Button>
      </div>
      {isPopupOpen && (
        <Popup
          onClose={handleClosePopup}
          title={(
            <FormattedMessage
              id="extras.alreadyAnswered"
              defaultMessage="This answer can no longer be changed."
              tagName="h2"
            />
          )}
        />
      )}
    </>
  );
};
export default AdviceCardActions;
